import type { SxProps, Theme } from '@mui/material';
import {
  Box, Divider, useTheme, styled,
} from '@mui/material';
import type { CSSProperties } from 'styled-components';
import React, { useMemo } from 'react';
import type { GlobalToken } from 'antd';
import { AnchorLink, Link } from '../../../component_library/components/Link';
import useFooter from './hooks/use-footer/use-footer';
import Typography, {
  TypographyVariant,
} from '../../../component_library/components/Typography';
import type {
  ITheme,
  ThemeColor,
} from '../../../component_library/styles/theme';
import { getCopyrightMessage } from '../../../utils/getCopyrightMessage';
import OperationButton from '../../OperationButton/OperationButton';
import StockPrice from '../../layout/stock-price-layout/StockPriceLayout';
import {
  DataElementId,
  ELEMENT_ID_DATA_TEST_ATTRIBUTE,
} from '../../../constants/data-element-tracking-ids';
import type { INavigationItemLink } from '../../../dotcms/dotcms-models/dotcms-navigation-item-link';
import {
  FOOTER_ID,
  FullPageWidthWrapper,
} from '../../../component_library/components/Grid';
import { useSettingBasedOnBreakpoints } from '../../../hooks/use-setting-based-on-breakpoints';
import type { HtmlFromDotcms } from '../../../dotcms/dotcms-models/converters-from-dotcms';
import { Page, PageUrlPath } from '../../../app/page-url-path';
import HtmlBox from '../../../component_library/components/HtmlBox/HtmlBox';
import { useGetRouteToIssuerPage } from '../../../app/get-route-to-page';
import { PoweredByCE } from '../../SVG/PoweredByCE';
import { useGetUserLocation } from '../../../hooks/use-get-user-location/use-get-user-location';
import { Footnote } from '../footnote';
import { useFooterContext } from './hooks/useFooterContext';
import {
  Button,
  ButtonVariant,
} from '../../../component_library/components/Button';
import { useThemeToken } from '../../../hooks/use-get-theme/use-get-theme';

interface ILanguageLink {
  languageCode: string;
  languageName: string;
}
interface ILanguagesSection {
  links: ReadonlyArray<ILanguageLink>;
  title: string;
  theme: ITheme;
  themeToken: GlobalToken;
  setLanguage: (languageCode: string) => void;
}

interface INavigationUserFooterLink {
  label: HtmlFromDotcms;
  path: string;
}

interface INavigationUserInformationSection {
  links: INavigationUserFooterLink[];
  title: string;
  theme: ITheme;
  textColor?: ThemeColor;
  additionalStyles?: CSSProperties;
}
interface INavigationSection {
  links: INavigationItemLink[];
  title: string;
  theme: ITheme;
  themeToken: GlobalToken;
  textColor?: ThemeColor;
  additionalStyles?: CSSProperties;
}

interface IBottomNavigationSection extends INavigationUserInformationSection {
  showSvgByCE?: boolean;
}

const LanguagesSection = ({
  links,
  title,
  theme,
  themeToken,
  setLanguage,
}: ILanguagesSection) => {
  const { colors } = theme;
  const StyledRoot = styled('div')({
    '& .title': {
      marginBottom: '16px',
    },
    '& .links-root': {
      display: 'flex',
      flexDirection: 'column',
    },
  });

  return (
    <StyledRoot>
      <div className="title">
        <Typography
          theme={theme}
          color={colors.grayscale.white}
          variant={TypographyVariant.SMALL_BODY_BOLD}
        >
          {title}
        </Typography>
      </div>
      <div className="links-root">
        {links.map((link) => (
          <Button
            key={link.languageCode}
            onClick={() => setLanguage(link.languageCode)}
            type="button"
            variant={ButtonVariant.FLAT}
            dataElementId={DataElementId.FOOTER_LANGUAGE_SELECT_BUTTON}
            themeToken={themeToken}
            inlineButton
            additionalStyles={{ marginBottom: `${themeToken.marginSM}px` }}
          >
            <Typography
              theme={theme}
              color={colors.grayscale.white}
              variant={TypographyVariant.LINKS_SMALL}
              additionalStyles={{
                fontWeight: '400',
              }}
            >
              {link.languageName}
            </Typography>
          </Button>
        ))}
      </div>
    </StyledRoot>
  );
};

const ResourcesSection = ({
  links,
  title,
  theme,
  themeToken,
  textColor = theme.colors.grayscale.white,
  additionalStyles = {},
}: INavigationSection) => {
  const StyledRoot = styled('div')({
    '& .links-root': {
      display: 'flex',
      flexDirection: 'column',
    },
    ...additionalStyles,
  });

  return (
    <StyledRoot className="footer-resources-section">
      <Typography
        theme={theme}
        color={textColor}
        variant={TypographyVariant.SMALL_BODY_BOLD}
        additionalStyles={{ marginBottom: '16px' }}
      >
        {title}
      </Typography>
      {!!links.length && (
        <div className="links-root">
          {links.map(({ path, label }) => (
            <Link
              key={`${path} - ${label}`}
              dataElementId={DataElementId.RESOURCE_LINK}
              variant={TypographyVariant.SMALL_BODY_REGULAR}
              color={textColor}
              to={path}
              themeToken={themeToken}
              additionalStyles={{
                '&:not(:last-child)': {
                  marginBottom: '16px',
                },
              }}
            >
              {label}
            </Link>
          ))}
        </div>
      )}
    </StyledRoot>
  );
};

const BottomMenuSection = ({
  links,
  title,
  theme,
  textColor = theme.colors.grayscale.white,
  additionalStyles = {},
  showSvgByCE = false,
}: IBottomNavigationSection) => {
  const muiTheme = useTheme();
  const themeToken = useThemeToken();
  const { isMobile } = useSettingBasedOnBreakpoints(muiTheme);
  const { isUsa } = useGetUserLocation();
  const StyledRoot = styled('div')({
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    [muiTheme.breakpoints.down('tablet')]: {
      justifyContent: 'center',
    },
    [muiTheme.breakpoints.down('tabletMini')]: {
      justifyContent: 'flex-start',
    },
    '& .footer-bottom-menu': {
      width: '100%',
      rowGap: '16px',
      flexWrap: 'wrap',
      justifyContent: 'center',
      display: 'flex',
      [muiTheme.breakpoints.down('tabletMini')]: {
        flexDirection: 'column',
        alignItems: 'flex-start',
      },
    },
    '& .footer-bottom-menu-title': {
      whiteSpace: 'nowrap',
      marginRight: '20px',
    },
    '& .footer-bottom-menu-links': {
      display: 'grid',
      gridTemplateColumns: 'repeat(4, minmax(100px, auto))',
      justifyContent: 'center',
      rowGap: '16px',
      columnGap: '40px',

      [muiTheme.breakpoints.down('desktop')]: {
        width: '100%',
        gridTemplateColumns: 'repeat(auto-fit, minmax(100px, auto))',
      },
      [muiTheme.breakpoints.down('tablet')]: {
        display: 'flex',
      },
      [muiTheme.breakpoints.down('tabletMini')]: {
        flexWrap: 'unset',
        flexDirection: 'column',
        alignItems: 'flex-start',
      },
    },
    '& .footer-bottom-menu-powered-by': {
      [muiTheme.breakpoints.up('tabletMini')]: {
        paddingLeft: '25px',
      },
    },
    ...additionalStyles,
  });
  const taxPageUrl = PageUrlPath[Page.TAX_INFORMATION];
  const getPath = (path: string): string =>
    path.substring(path.lastIndexOf('/'));

  const filteredLinksIfNotUSHideTaxLink = !isUsa
    ? links.filter((link) => getPath(link.path) !== getPath(taxPageUrl))
    : links;

  return (
    <StyledRoot>
      <Box className="footer-bottom-menu">
        <Box className="footer-bottom-menu-title">
          <Typography
            theme={theme}
            color={textColor}
            variant={TypographyVariant.DISCLAIMER}
          >
            {title}
          </Typography>
        </Box>
        {filteredLinksIfNotUSHideTaxLink &&
          !!filteredLinksIfNotUSHideTaxLink?.length && (
            <Box className="footer-bottom-menu-links">
              {filteredLinksIfNotUSHideTaxLink.map(({ path, label }) => (
                <Link
                  key={`${path}`}
                  variant={TypographyVariant.DISCLAIMER}
                  color={textColor}
                  to={path}
                  additionalStyles={{
                    textAlign: 'center',
                    marginBottom: isMobile ? '12px' : '0',
                  }}
                  dataElementId={DataElementId.FOOTER_MENU_ITEM_LINK}
                  themeToken={themeToken}
                >
                  <HtmlBox html={label} />
                </Link>
              ))}
            </Box>
        )}

        {showSvgByCE && (
          <Box className="footer-bottom-menu-powered-by">
            <PoweredByCE color={textColor} />
          </Box>
        )}
      </Box>
    </StyledRoot>
  );
};

export enum FooterDisplayMode {
  FullWidth,
  LightFullWidth,
  LightEmbedded,
}

interface IProps {
  mode?: FooterDisplayMode;
}

const Footer: React.FC<IProps> = React.memo<IProps>(
  // eslint-disable-next-line react/prop-types
  ({ mode = FooterDisplayMode.FullWidth }) => {
    const {
      theme,
      navigationMenu,
      navigationMenuLoading,
      navigationMenuError,
      setLocale: setLanguage,
      availableLanguages,
    } = useFooter();
    const muiTheme = useTheme();
    const themeToken = useThemeToken();
    const { isDesktop } = useSettingBasedOnBreakpoints(muiTheme);
    const privacyPolicyPath = useGetRouteToIssuerPage(Page.PRIVACY_POLICY);
    const taxInformationPath = useGetRouteToIssuerPage(Page.TAX_INFORMATION);
    const importantInformationPath = useGetRouteToIssuerPage(
      Page.IMPORTANT_INFORMATION,
    );
    const termOfServicePath = useGetRouteToIssuerPage(Page.TERM_OF_SERVICE);
    const {
      footnotes,
      isLightFooterMode,
      cmsData: footerData,
      cmsLoading: footerDataLoading,
    } = useFooterContext();

    const footnotesArray = useMemo(
      () =>
        footnotes &&
        footnotes.map((footnote) => (
          // eslint-disable-next-line no-underscore-dangle
          <Footnote key={footnote.__html} content={footnote} />
        )),
      [footnotes],
    );

    const FooterStyled = styled('div')({
      width: '100%',
      padding: '64px 0 24px',
      boxSizing: 'border-box',
      backgroundColor: theme.colors.secondary,
      '& .footer-bottom-menu': {
        marginTop: '64px',
        [muiTheme.breakpoints.down('tablet')]: {
          marginTop: '40px',
        },
      },
      '& .footer-info-wrapper': {
        display: 'flex',
        width: '100%',
        [muiTheme.breakpoints.down('desktop')]: {
          flexDirection: 'column',
        },
      },
      '& .links-root': {
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',

        [muiTheme.breakpoints.down('desktop')]: {
          maxWidth: '617px',
        },
        [muiTheme.breakpoints.down('tablet')]: {
          maxWidth: '503px',
        },
        [muiTheme.breakpoints.down('tabletMini')]: {
          flexDirection: 'column',
        },
      },
      '& .questions': {
        '& .title': {
          marginBottom: '16px',
        },
        [muiTheme.breakpoints.down('tabletMini')]: {
          marginTop: '40px',
        },
      },
      '& .operation-button': {
        marginTop: '32px',
      },
      '& .footer-resources-section': {
        [muiTheme.breakpoints.down('tabletMini')]: {
          marginBottom: '40px',
        },
      },
      '& .footer-divider': {
        borderColor: theme.colors.grayscale.white,
        margin: '0 64px',
        [muiTheme.breakpoints.down('desktop')]: {
          margin: '40px 0',
        },
      },
    });

    const FooterLightFullWidthStyled = styled('div')({
      width: '100%',
      paddingBottom: '23px',
      marginTop: '40px',
    });

    const FooterLightEmbeddedStyled = styled('div')({
      width: '100%',
      marginTop: '40px',
    });

    const FOOTNOTE_STYLE = {
      marginTop: '40px',
      justifyContent: 'stretch',
      '>:not(:last-of-type)': {
        marginBottom: '16px',
      },
      [muiTheme.breakpoints.down('tabletMini')]: {
        marginTop: '40px',
        order: 2,
      },
    };

    const FOOTNOTE_LIGHT_STYLE = {
      marginTop: '40px',
      marginBottom: '64px',
      justifyContent: 'stretch',
      '>:not(:last-of-type)': {
        marginBottom: '16px',
      },
    };

    const FOOTER_LIGHT_LAYOUT_STYLE: SxProps<Theme> = {
      marginTop: 'auto',
      width: isLightFooterMode ? '100%' : 'auto',
      minHeight: '24px',
      position: 'relative',
      marginBottom: '24px',
      display: 'flex',
      justifyContent: 'center',
      alignItem: 'flex-end',
      '& .footer-bottom-menu': {
        marginTop: '0px',
      },
      [muiTheme.breakpoints.down('tablet')]: {
        '& .footer-bottom-menu': {
          flexDirection: 'row',
          alignItems: 'flex-start',
          justifyContent: 'center',
        },
        '& .footer-bottom-menu-title': {
          margin: '0',
        },
        '& .footer-bottom-menu .footer-bottom-menu-links': {
          margin: '0 0 0 20px',
        },
      },
      [muiTheme.breakpoints.down('tabletMini')]: {
        justifyContent: 'center',
        width: '100%',
        padding: '0 24px',
        marginBottom: mode === FooterDisplayMode.LightFullWidth ? 0 : undefined,
        '& .footer-bottom-menu': {
          flexDirection: 'column',
          justifyContent: 'center',
          flexWrap: 'unset',
        },
        '& .footer-bottom-menu > div:last-child': {
          order: 3,
        },
        '& .footer-bottom-menu .footer-bottom-menu-links': {
          margin: '0 0 8px',
          order: 1,
          '& a': {
            marginBottom: 0,
          },
        },
        '& .footer-bottom-menu-title': {
          order: 2,
        },
      },
    };

    if (navigationMenuLoading || !footerData || footerDataLoading) {
      return null;
    }

    if (navigationMenuError || !navigationMenu || !footerData) {
      return <div>Something went wrong</div>;
    }

    const resourceLinks = [
      ...(navigationMenu?.resourcesLinksGroup ?? []),
      ...(navigationMenu?.cashlessParticipationLinkGroup ?? []),
    ];
    const titleResourceSection = footerData?.titleForResourceSection;
    const titleLanguagesSection = footerData?.titleForLanguagesSection;
    const question = footerData?.question;
    const copyrightMessage = getCopyrightMessage();

    const bottomMenuSectionLinks = [
      {
        label: footerData?.privacyPolicyLinkTitle || { __html: '' },
        path: privacyPolicyPath,
      },
      {
        label: footerData?.taxInformationLinkTitle || { __html: '' },
        path: taxInformationPath,
      },
      {
        label: footerData?.importantInformationTitle || { __html: '' },
        path: importantInformationPath,
      },
      {
        label: footerData?.termsOfServiceTitle || { __html: '' },
        path: termOfServicePath,
      },
    ];

    const linksSection = (
      <BottomMenuSection
        title={copyrightMessage}
        links={bottomMenuSectionLinks}
        theme={theme}
        textColor={theme.colors.grayscale.oldSilver}
        additionalStyles={{ justifyContent: 'flex-start' }}
      />
    );

    if (isLightFooterMode) {
      return (
        <Box
          id={FOOTER_ID}
          sx={FOOTER_LIGHT_LAYOUT_STYLE}
          style={{
            justifyContent: 'center',
          }}
        >
          {linksSection}
        </Box>
      );
    }

    if (
      mode === FooterDisplayMode.LightFullWidth ||
      mode === FooterDisplayMode.LightEmbedded
    ) {
      return mode === FooterDisplayMode.LightFullWidth ? (
        <FooterLightFullWidthStyled id={FOOTER_ID}>
          <FullPageWidthWrapper>
            {footnotesArray.length > 0 && (
              <Box sx={FOOTNOTE_LIGHT_STYLE}>{footnotesArray}</Box>
            )}
            <Box sx={FOOTER_LIGHT_LAYOUT_STYLE}>{linksSection}</Box>
          </FullPageWidthWrapper>
        </FooterLightFullWidthStyled>
      ) : (
        <FooterLightEmbeddedStyled id={FOOTER_ID}>
          {footnotesArray.length > 0 && (
            <Box sx={FOOTNOTE_LIGHT_STYLE}>{footnotesArray}</Box>
          )}
          <Box sx={FOOTER_LIGHT_LAYOUT_STYLE}>{linksSection}</Box>
        </FooterLightEmbeddedStyled>
      );
    }

    return (
      <FooterStyled
        id={FOOTER_ID}
        {...{ [ELEMENT_ID_DATA_TEST_ATTRIBUTE]: DataElementId.FOOTER }}
      >
        <FullPageWidthWrapper>
          <Box className="footer-info-wrapper">
            <Box className="stock-price">
              <StockPrice fontColor="white" />
              <OperationButton
                dataElementId={DataElementId.FOOTER_BUTTON_ENROLL_NOW}
              />
            </Box>
            <Divider
              orientation={isDesktop ? 'vertical' : 'horizontal'}
              variant="fullWidth"
              className="footer-divider"
              sx={{ height: 'auto' }}
            />
            <Box className="links-root">
              {resourceLinks &&
                resourceLinks.length > 0 &&
                !!titleResourceSection && (
                  <ResourcesSection
                    title={titleResourceSection}
                    links={resourceLinks}
                    theme={theme}
                    themeToken={themeToken}
                  />
              )}
              {availableLanguages.length > 1 && (
                <LanguagesSection
                  title={titleLanguagesSection || ''}
                  links={availableLanguages}
                  setLanguage={setLanguage}
                  theme={theme}
                  themeToken={themeToken}
                />
              )}
              {question && (
                <Box className="questions">
                  <Box className="title">
                    <Typography
                      theme={theme}
                      color={theme.colors.grayscale.white}
                      variant={TypographyVariant.SMALL_BODY_BOLD}
                    >
                      {question.label}
                    </Typography>
                  </Box>
                  <AnchorLink
                    dataElementId={DataElementId.FOOTER_QUESTIONS_MAIL_BUTTON}
                    href={`mailto:${question.email}`}
                    variant={TypographyVariant.SMALL_BODY_REGULAR}
                    color={theme.colors.grayscale.white}
                    themeToken={themeToken}
                  >
                    {question.email}
                  </AnchorLink>
                </Box>
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box sx={FOOTNOTE_STYLE}>{footnotesArray}</Box>
            {footerData?.patentNumberNote && (
              <Box sx={FOOTNOTE_STYLE}>
                <Footnote content={footerData?.patentNumberNote} />
              </Box>
            )}
            <BottomMenuSection
              title={copyrightMessage}
              links={bottomMenuSectionLinks}
              theme={theme}
            />
          </Box>
        </FullPageWidthWrapper>
      </FooterStyled>
    );
  },
);
Footer.displayName = 'Footer';

export default Footer;
